import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Search, Zap } from 'lucide-react';

const MAX_JOBS_PER_FETCH = 100;
const DEFAULT_JOBS_PER_FETCH = 50;
const CREDITS_PER_JOB = 1;

// "Posted within" windows (hours), per fetch source.
// ATS jobs carry real hour-level posting timestamps, so hour windows work there.
// 1h/2h are viable because the ATS scraper runs every 30 minutes — a 1h window is
// always backed by at least one completed scrape.
const ATS_POSTED_WITHIN_OPTIONS = [
    { label: '1h', hours: 1 },
    { label: '2h', hours: 2 },
    { label: '3h', hours: 3 },
    { label: '6h', hours: 6 },
    { label: '12h', hours: 12 },
    { label: '24h', hours: 24 },
    { label: '2d', hours: 48 },
    { label: '3d', hours: 72 },
    { label: '7d', hours: 168 },
    { label: '14d', hours: 336 }
];
const DEFAULT_ATS_POSTED_WITHIN_HOURS = 1;
// Preference/keyword fetches are served by JSearch, whose posting dates are
// day-granular — offer only its native buckets.
const JSEARCH_POSTED_WITHIN_OPTIONS = [
    { label: 'Today', hours: 24 },
    { label: '3 days', hours: 72 },
    { label: 'Week', hours: 168 },
    { label: 'Month', hours: 720 }
];
const DEFAULT_JSEARCH_POSTED_WITHIN_HOURS = 168;

// JSearch-backed options ("By My Preferences" / "By Keyword Search") are hidden from the
// modal for now — flip to true to bring them back. All JSearch code paths are kept intact.
const SHOW_JSEARCH_OPTIONS = false;

const FetchJobsModal = ({ isOpen, onClose, onFetchByPreference, onFetchByKeyword, onFetchATSOnly, onFetchATSByKeyword, loading, creditsBalance = 0 }) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [jobCount, setJobCount] = useState(DEFAULT_JOBS_PER_FETCH);
    const [postedWithinHours, setPostedWithinHours] = useState(DEFAULT_ATS_POSTED_WITHIN_HOURS);
    const [keywords, setKeywords] = useState({
        keyword: '',
        industry: '',
        seniorityLevel: '',
        location: '',
        role: '',
        skills: ''
    });

    // The most jobs the user can afford / is allowed this fetch. ATS fetches are FREE
    // (served from our own scraped data), so they're never capped by credit balance.
    const affordableMax = Math.max(1, Math.min(MAX_JOBS_PER_FETCH, Math.floor(Number(creditsBalance) || 0)));
    const isFreeOption = selectedOption === 'ats-only' || selectedOption === 'ats-keyword';
    const maxForSelected = isFreeOption ? MAX_JOBS_PER_FETCH : affordableMax;

    useEffect(() => {
        if (isOpen) {
            setSelectedOption(null);
            // Default to 50, capped by what the user can afford (max 100 via the input).
            setJobCount(Math.max(1, Math.min(DEFAULT_JOBS_PER_FETCH, Math.floor(Number(creditsBalance) || 0) || DEFAULT_JOBS_PER_FETCH)));
            setPostedWithinHours(DEFAULT_ATS_POSTED_WITHIN_HOURS);
            setKeywords({
                keyword: '',
                industry: '',
                seniorityLevel: '',
                location: '',
                role: '',
                skills: ''
            });
        }
    }, [isOpen, creditsBalance]);

    const clampCount = (n) => Math.max(1, Math.min(maxForSelected, Math.floor(Number(n) || 1)));

    if (!isOpen) return null;

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        // Each source has its own window set — if the current pick isn't valid for the
        // newly-selected source, reset to that source's default.
        const isAtsSource = option === 'ats-only' || option === 'ats-keyword';
        const opts = isAtsSource ? ATS_POSTED_WITHIN_OPTIONS : JSEARCH_POSTED_WITHIN_OPTIONS;
        if (!opts.some((o) => o.hours === postedWithinHours)) {
            setPostedWithinHours(isAtsSource ? DEFAULT_ATS_POSTED_WITHIN_HOURS : DEFAULT_JSEARCH_POSTED_WITHIN_HOURS);
        }
    };

    const RadioIcon = ({ isSelected, tone = 'indigo' }) => {
        const shell =
            tone === 'orange'
                ? (isSelected ? 'border-orange-500 bg-orange-500' : 'border-slate-300 bg-white')
                : tone === 'green'
                ? (isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white')
                : (isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 bg-white');

        return (
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${shell}`}>
                {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-white"></div>}
            </div>
        );
    };

    const handleFetch = () => {
        const count = clampCount(jobCount);
        if (selectedOption === 'preference') {
            onFetchByPreference(count, postedWithinHours);
        } else if (selectedOption === 'keyword') {
            onFetchByKeyword(keywords, count, postedWithinHours);
        } else if (selectedOption === 'ats-only') {
            if (typeof onFetchATSOnly === 'function') {
                onFetchATSOnly(count, postedWithinHours);
            }
        } else if (selectedOption === 'ats-keyword') {
            if (typeof onFetchATSByKeyword === 'function') {
                onFetchATSByKeyword(keywords, count, postedWithinHours);
            }
        }
    };

    const isKeywordValid = keywords.keyword || keywords.industry || keywords.seniorityLevel || keywords.location || keywords.role || keywords.skills;

    // Selected-chip accent per option, so the time filter matches its card's color.
    const selectedChipClass =
        selectedOption === 'ats-only'
            ? 'border-orange-500 bg-orange-500 text-white'
            : selectedOption === 'keyword' || selectedOption === 'ats-keyword'
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-indigo-600 bg-indigo-600 text-white';

    // Count selector + posted-within window + credit cost, rendered inside the
    // currently-selected option card.
    const renderCountSelector = () => (
        <div className="mt-4 pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4">
                <p className="text-sm font-semibold text-slate-800 font-dmsans text-left">Posted within</p>
                <p className="text-xs text-slate-500 font-dmsans mt-0.5 text-left">Only fetch jobs posted in the last…</p>
                <div className="flex flex-wrap gap-2 mt-2">
                    {(isFreeOption ? ATS_POSTED_WITHIN_OPTIONS : JSEARCH_POSTED_WITHIN_OPTIONS).map((opt) => (
                        <button
                            key={opt.hours}
                            type="button"
                            onClick={() => setPostedWithinHours(opt.hours)}
                            disabled={loading}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold font-dmsans border transition-colors ${
                                postedWithinHours === opt.hours
                                    ? selectedChipClass
                                    : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-left">
                    <p className="text-sm font-semibold text-slate-800 font-dmsans text-left">How many jobs to fetch?</p>
                    <p className="text-xs text-slate-500 font-dmsans mt-0.5 text-left">Up to {maxForSelected} this fetch at a time</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setJobCount((c) => clampCount(c - 5))}
                        disabled={loading || clampCount(jobCount) <= 1}
                        className="w-8 h-8 rounded-lg border border-slate-300 bg-white text-slate-700 font-bold disabled:opacity-40 hover:bg-slate-100"
                        aria-label="Decrease"
                    >
                        −
                    </button>
                    <input
                        type="number"
                        min={1}
                        max={affordableMax}
                        value={jobCount}
                        onChange={(e) => {
                            // Clamp WHILE typing (allowing a momentarily-empty field) so the
                            // shown number always matches what will actually be fetched:
                            // min 1, max = lower of 50 and the user's credit balance.
                            const v = e.target.value;
                            setJobCount(v === '' ? '' : clampCount(v));
                        }}
                        onBlur={() => setJobCount((c) => clampCount(c))}
                        disabled={loading}
                        className="w-16 text-center px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-sm font-semibold font-dmsans focus:outline-none focus:border-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => setJobCount((c) => clampCount(c + 5))}
                        disabled={loading || clampCount(jobCount) >= affordableMax}
                        className="w-8 h-8 rounded-lg border border-slate-300 bg-white text-slate-700 font-bold disabled:opacity-40 hover:bg-slate-100"
                        aria-label="Increase"
                    >
                        +
                    </button>
                </div>
            </div>
            {isFreeOption ? (
                <div className="mt-3 flex items-center justify-between text-xs font-dmsans">
                    <span className="text-emerald-700 font-semibold">
                        Free — <span className="font-normal text-slate-600">no credits used, sourced from our own ATS index</span>
                    </span>
                    <span className="font-bold text-emerald-600">0 credits</span>
                </div>
            ) : (
                <>
                    <div className="mt-3 flex items-center justify-between text-xs font-dmsans">
                        <span className="text-slate-600">
                            Costs <span className="font-semibold text-slate-800">{CREDITS_PER_JOB} credit</span> per job
                        </span>
                        <span className="text-slate-700">
                            Up to <span className="font-bold text-blue-700">{clampCount(jobCount) * CREDITS_PER_JOB} credits</span>
                            <span className="text-slate-400"> · {Math.floor(Number(creditsBalance) || 0)} available</span>
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-dmsans mt-1.5">You're only charged for jobs actually added to your list.</p>
                </>
            )}
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl relative overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
                    aria-label="Close modal"
                    disabled={loading}
                >
                    <X size={22} />
                </button>

                <div className="px-6 sm:px-10 pt-8 pb-6">
                    <div className="flex items-center justify-center mb-2">
                        <div className="bg-blue-50 p-2 rounded-full mr-3">
                            <TrendingUp size={20} className="text-blue-600" />
                        </div>
                        <h2 className="text-2xl font-dmsans font-bold text-gray-800">Fetch More Jobs</h2>
                    </div>
                    <p className="text-center text-sm text-slate-600 font-dmsans max-w-xl mx-auto">
                        Choose how you'd like to find more job opportunities.
                    </p>
                </div>

                <div className="px-6 sm:px-10 pb-6 max-h-[70vh] overflow-y-auto">

                    {/* Option 1: Fetch Latest ATS Jobs (recommended) */}
                    <div
                        className={`p-5 rounded-2xl border cursor-pointer transition-all duration-200 mb-4 ${
                            selectedOption === 'ats-only'
                                ? 'border-orange-500 bg-white text-slate-900 shadow-lg shadow-orange-100'
                                : 'border-slate-200 bg-white text-slate-900 hover:border-orange-300 hover:shadow-md'
                        }`}
                        onClick={() => handleOptionSelect('ats-only')}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <Zap size={20} className="text-orange-500" />
                                    <h3 className="text-lg font-bold font-dmsans text-gray-800">
                                        Fetch Latest ATS Jobs by Your Preferences
                                    </h3>
                                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-emerald-500 text-white">
                                        Free
                                    </span>
                                </div>
                                <p className="text-sm font-dmsans mb-3 text-slate-600">
                                    Discover jobs posted directly on company career pages — often before they hit LinkedIn and other job boards
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {['Early Access', 'Less Competition', 'Fresh Listings', 'Direct Source'].map((t) => (
                                        <span key={t} className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-600">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                {selectedOption === 'ats-only' && (
                                    <p className="mt-3 text-xs font-dmsans text-orange-700 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2 text-left">
                                        We'll fetch ATS jobs matched to <span className="font-semibold">your profile preferences</span> — your target roles and industries.
                                    </p>
                                )}
                                {selectedOption === 'ats-only' && renderCountSelector()}
                            </div>
                            <RadioIcon isSelected={selectedOption === 'ats-only'} tone="orange" />
                        </div>
                    </div>

                    {/* Option 2: By Preference (JSearch) — hidden via SHOW_JSEARCH_OPTIONS, code kept */}
                    {SHOW_JSEARCH_OPTIONS && (
                    <div
                        className={`p-5 rounded-2xl border cursor-pointer transition-all duration-200 mb-4 ${
                            selectedOption === 'preference'
                                ? 'border-indigo-400 bg-white text-slate-900 shadow-lg shadow-indigo-100'
                                : 'border-slate-200 bg-white text-slate-900 hover:border-indigo-300 hover:shadow-md'
                        }`}
                        onClick={() => handleOptionSelect('preference')}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingUp size={20} className="text-indigo-600" />
                                    <h3 className="text-lg font-bold font-dmsans text-gray-800">
                                        By My Preferences
                                    </h3>
                                </div>
                                <p className="text-sm font-dmsans mb-3 text-slate-600">
                                    Find jobs based on your profile preferences, skills, and experience level
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                        Smart Matching
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                        Personalized Results
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                        Best Fit
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                        Resume
                                    </span>
                                </div>
                            </div>
                            <RadioIcon isSelected={selectedOption === 'preference'} tone="indigo" />
                        </div>
                        {selectedOption === 'preference' && renderCountSelector()}
                    </div>
                    )}

                    {/* Option 3: By Keyword (JSearch) — hidden via SHOW_JSEARCH_OPTIONS, code kept */}
                    {SHOW_JSEARCH_OPTIONS && (
                    <div
                        className={`p-5 rounded-2xl border cursor-pointer transition-all duration-200 mb-4 ${
                            selectedOption === 'keyword'
                                ? 'border-emerald-500 bg-white text-slate-900 shadow-lg shadow-emerald-100'
                                : 'border-slate-200 bg-white text-slate-900 hover:border-emerald-300 hover:shadow-md'
                        }`}
                        onClick={() => handleOptionSelect('keyword')}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <Search size={20} className="text-emerald-600" />
                                    <h3 className="text-lg font-bold font-dmsans text-gray-800">
                                        By Keyword Search
                                    </h3>
                                </div>
                                <p className="text-sm font-dmsans mb-3 text-slate-600">
                                    Search by general keyword or use specific filters like industry, seniority, location, role, or skills
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {['Targeted Search', 'Custom Filters', 'Role & Skills', 'Precise Match'].map((t) => (
                                        <span key={t} className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                                            {t}
                                        </span>
                                    ))}
                                </div>

                                {/* Keyword Input Fields */}
                                {selectedOption === 'keyword' && (
                                    <div className="space-y-3 mt-4" onClick={(e) => e.stopPropagation()}>
                                        {/* General Keyword Field - Full Width */}
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Keyword (General Search)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., Partnerships, Account Manager, Data Science"
                                                value={keywords.keyword}
                                                onChange={(e) => setKeywords({...keywords, keyword: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                            <p className="text-xs mt-1 font-dmsans italic text-slate-500">
                                                Search across job titles, descriptions, and company names
                                            </p>
                                        </div>

                                        {/* Divider */}
                                        <div className="flex items-center gap-2 my-3">
                                            <div className="flex-1 h-px bg-slate-200"></div>
                                            <span className="text-xs font-dmsans text-slate-500">Add filters below to refine</span>
                                            <div className="flex-1 h-px bg-slate-200"></div>
                                        </div>

                                        {/* Existing Fields in Grid */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Industry
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., Technology, Healthcare, Finance"
                                                value={keywords.industry}
                                                onChange={(e) => setKeywords({...keywords, industry: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Seniority Level
                                            </label>
                                            <select
                                                value={keywords.seniorityLevel}
                                                onChange={(e) => setKeywords({...keywords, seniorityLevel: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            >
                                                <option value="">Select Level</option>
                                                <option value="Entry">Entry</option>
                                                <option value="Junior">Junior</option>
                                                <option value="Mid">Mid</option>
                                                <option value="Senior">Senior</option>
                                                <option value="Executive">Executive</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Location
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., San Francisco, Remote"
                                                value={keywords.location}
                                                onChange={(e) => setKeywords({...keywords, location: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Role/Position
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., Software Engineer, Product Manager"
                                                value={keywords.role}
                                                onChange={(e) => setKeywords({...keywords, role: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Skills
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., React, Python, AWS"
                                                value={keywords.skills}
                                                onChange={(e) => setKeywords({...keywords, skills: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                        </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <RadioIcon isSelected={selectedOption === 'keyword'} tone="green" />
                        </div>
                        {selectedOption === 'keyword' && renderCountSelector()}
                    </div>
                    )}

                    {/* Option: Fetch Latest ATS Jobs by Keywords */}
                    <div
                        className={`p-5 rounded-2xl border cursor-pointer transition-all duration-200 mb-4 ${
                            selectedOption === 'ats-keyword'
                                ? 'border-emerald-500 bg-white text-slate-900 shadow-lg shadow-emerald-100'
                                : 'border-slate-200 bg-white text-slate-900 hover:border-emerald-300 hover:shadow-md'
                        }`}
                        onClick={() => handleOptionSelect('ats-keyword')}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <Search size={20} className="text-emerald-600" />
                                    <h3 className="text-lg font-bold font-dmsans text-gray-800">
                                        Fetch Latest ATS Jobs by Keywords
                                    </h3>
                                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-emerald-500 text-white">
                                        Free
                                    </span>
                                </div>
                                <p className="text-sm font-dmsans mb-3 text-slate-600">
                                    Search fresh ATS jobs from company career pages by keyword, or refine with industry, seniority, location, role, or skills
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {['Targeted Search', 'Custom Filters', 'Direct Source', 'Fresh Listings'].map((t) => (
                                        <span key={t} className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                                            {t}
                                        </span>
                                    ))}
                                </div>

                                {/* Keyword Input Fields */}
                                {selectedOption === 'ats-keyword' && (
                                    <div className="space-y-3 mt-4" onClick={(e) => e.stopPropagation()}>
                                        {/* General Keyword Field - Full Width */}
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Keyword (General Search)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., Partnerships, Account Manager, Data Science"
                                                value={keywords.keyword}
                                                onChange={(e) => setKeywords({...keywords, keyword: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                            <p className="text-xs mt-1 font-dmsans italic text-slate-500">
                                                Search across job titles, descriptions, and company names
                                            </p>
                                        </div>

                                        {/* Divider */}
                                        <div className="flex items-center gap-2 my-3">
                                            <div className="flex-1 h-px bg-slate-200"></div>
                                            <span className="text-xs font-dmsans text-slate-500">Add filters below to refine</span>
                                            <div className="flex-1 h-px bg-slate-200"></div>
                                        </div>

                                        {/* Filter Fields in Grid */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Industry
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., Technology, Healthcare, Finance"
                                                value={keywords.industry}
                                                onChange={(e) => setKeywords({...keywords, industry: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Seniority Level
                                            </label>
                                            <select
                                                value={keywords.seniorityLevel}
                                                onChange={(e) => setKeywords({...keywords, seniorityLevel: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            >
                                                <option value="">Select Level</option>
                                                <option value="Entry">Entry</option>
                                                <option value="Junior">Junior</option>
                                                <option value="Mid">Mid</option>
                                                <option value="Senior">Senior</option>
                                                <option value="Executive">Executive</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Location
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., San Francisco, Remote"
                                                value={keywords.location}
                                                onChange={(e) => setKeywords({...keywords, location: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Role/Position
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., Software Engineer, Product Manager"
                                                value={keywords.role}
                                                onChange={(e) => setKeywords({...keywords, role: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold mb-1 font-dmsans text-slate-700">
                                                Skills
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., React, Python, AWS"
                                                value={keywords.skills}
                                                onChange={(e) => setKeywords({...keywords, skills: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg text-sm font-dmsans border bg-white border-emerald-200 focus:outline-none"
                                            />
                                        </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <RadioIcon isSelected={selectedOption === 'ats-keyword'} tone="green" />
                        </div>
                        {selectedOption === 'ats-keyword' && renderCountSelector()}
                    </div>

                    {/* Info Box */}
                    <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                        <p className="text-xs text-slate-700 font-dmsans">
                            <span className="font-semibold text-slate-800">Tip:</span>{' '}
                            {selectedOption === 'preference'
                                ? 'Preference-based fetching uses your profile to find the best matches automatically.'
                                : selectedOption === 'keyword'
                                ? 'Start with a general Keyword, then add filters (industry, seniority, location, role, skills) to narrow it down. The more you add, the more specific the results.'
                                : selectedOption === 'ats-only'
                                ? 'ATS jobs are sourced directly from company career pages and ATS platforms, so you can find fresh listings that may not show up on job boards yet.'
                                : selectedOption === 'ats-keyword'
                                ? 'Start with a general Keyword, then add filters (industry, seniority, location, role, skills) to narrow it down. Jobs come straight from company career pages and ATS platforms — free, no credits used.'
                                : 'Select an option above to continue.'}
                        </p>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-3 border-t px-6 sm:px-10 py-4 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 border border-gray-300 text-gray-700 font-dmsans font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleFetch}
                        disabled={!selectedOption || ((selectedOption === 'keyword' || selectedOption === 'ats-keyword') && !isKeywordValid) || loading}
                        className={`px-5 py-2.5 font-dmsans font-semibold rounded-lg transition-colors shadow-md ${
                            !selectedOption || ((selectedOption === 'keyword' || selectedOption === 'ats-keyword') && !isKeywordValid) || loading
                                ? 'bg-indigo-200 text-indigo-400 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                </svg>
                                Fetching...
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <Search size={16} />
                                Fetch Jobs
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FetchJobsModal;

